// Firmen-Akte — chronologische Akte je Mandant:
// Besprechungsnotizen (Google Calendar), E-Mails (Gmail), manuelle Dokumente.

import React, { useCallback, useEffect, useState } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import {
  loadAkte, importBesprechungsnotizen, importEmails,
  addManuellerEintrag, deleteAkteEintrag, toggleAkteErledigt,
  belegeAlsCsv, downloadCsv, type ImportErgebnis,
} from '../../services/akteService';
import { loadSetting } from '../../services/settingsService';
import { CaptureModal } from '../../components/CaptureModal';
import ErinnerungButton from '../../components/ErinnerungButton';
import { loadProtokolle } from '../../services/protokollService';
import { useFocusStore } from '../../store/focusStore';
import type { Protokoll } from '../../db/schema';
import { useHandoffStore } from '../../store/handoffStore';
import type { AkteEintrag } from '../../db/schema';
import type { MandantID } from '../../types/branded';

const FORMULAR_CHIPS = [
  { typ: 'ASA-Protokoll',      icon: '🛡' },
  { typ: 'Befundbrief',        icon: '📝' },
  { typ: 'Eignungszeugnis',    icon: '✅' },
] as const;

const TYP_META: Record<AkteEintrag['typ'], { icon: string; label: string; cls: string }> = {
  BESPRECHUNG: { icon: '🗓', label: 'Besprechung', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400' },
  EMAIL:       { icon: '✉',  label: 'E-Mail',      cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400' },
  DOKUMENT:    { icon: '📄', label: 'Dokument',    cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' },
  BELEG:       { icon: '🧾', label: 'Beleg',       cls: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400' },
};

function fmtDatum(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-AT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// ── Eintrag-Karte ──────────────────────────────────────────────────────────

function EintragKarte({ eintrag, mandantName, onDelete, onToggleErledigt, onFormular, onBegehung }: {
  eintrag: AkteEintrag;
  mandantName: string;
  onDelete: (e: AkteEintrag) => void;
  onToggleErledigt: (e: AkteEintrag) => void;
  onFormular: (e: AkteEintrag, formularTyp: string) => void;
  onBegehung: (e: AkteEintrag) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const meta = TYP_META[eintrag.typ];
  const hasSchritt = eintrag.naechster_schritt !== undefined && eintrag.naechster_schritt !== '';
  const erledigt = eintrag.erledigt ?? false;

  return (
    <div className="card p-0 overflow-hidden">
      <button onClick={() => { setOpen(o => !o); }}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${meta.cls}`}>
          {meta.icon} {meta.label}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {eintrag.titel}
            {eintrag.betrag_cents !== undefined && (
              <span className="ml-1 text-gray-500">· {(eintrag.betrag_cents / 100).toFixed(2).replace('.', ',')} €</span>
            )}
          </p>
          <p className="text-xs text-gray-400 truncate">
            {mandantName} · {fmtDatum(eintrag.datum)}
            {eintrag.absender !== undefined ? ` · von ${eintrag.absender}` : ''}
          </p>
        </div>
        <span className="text-gray-400 text-xs flex-shrink-0">{open ? '▲' : '▼'}</span>
      </button>

      {/* KI-Vorschlag: nächster Schritt als abhakbare Aufgabe */}
      {hasSchritt && (
        <div className={`flex items-center gap-2 px-4 py-2 border-t border-gray-100 dark:border-gray-800
          ${erledigt ? 'bg-gray-50 dark:bg-gray-800/30' : 'bg-blue-50/50 dark:bg-blue-950/10'}`}>
          <input type="checkbox" checked={erledigt}
            onChange={() => { onToggleErledigt(eintrag); }}
            className="rounded border-gray-300 text-green-600 flex-shrink-0"
            title="Als erledigt markieren" />
          <span className={`text-xs flex-1 ${erledigt ? 'line-through text-gray-400' : 'text-blue-700 dark:text-blue-300'}`}>
            🎯 {eintrag.naechster_schritt}
          </span>
          {!erledigt && (
            <ErinnerungButton
              titel={eintrag.naechster_schritt ?? eintrag.titel}
              typ="TODO"
              mandantId={eintrag.mandant_id}
              quelle={`akte:${eintrag.id}`}
              label="erinnern"
              className="text-xs text-gray-400 hover:text-amber-600 flex-shrink-0"
            />
          )}
        </div>
      )}
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-800 space-y-3">
          {eintrag.anhang_b64 !== undefined && eintrag.anhang_mime?.startsWith('image/') === true && (
            <img src={`data:${eintrag.anhang_mime};base64,${eintrag.anhang_b64}`}
              alt={eintrag.titel}
              className="max-h-72 rounded-lg border border-gray-200 dark:border-gray-800 object-contain" />
          )}
          {eintrag.anhang_b64 !== undefined && eintrag.anhang_mime === 'application/pdf' && (
            <a href={`data:application/pdf;base64,${eintrag.anhang_b64}`}
              download={`${eintrag.titel}.pdf`}
              className="text-sm text-blue-700 dark:text-blue-400 font-medium hover:underline">
              📄 PDF-Anhang herunterladen
            </a>
          )}
          <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans max-h-80 overflow-y-auto">
            {eintrag.inhalt || '(kein Inhalt)'}
          </pre>
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-gray-500">Als Formular weiterbearbeiten:</p>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => { onBegehung(eintrag); }}
                className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700
                           text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600
                           transition-colors">
                🔍 Begehung
              </button>
              {FORMULAR_CHIPS.map(f => (
                <button key={f.typ} onClick={() => { onFormular(eintrag, f.typ); }}
                  className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700
                             text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600
                             transition-colors">
                  {f.icon} {f.typ}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => { onDelete(eintrag); }}
            className="text-xs text-red-500 hover:text-red-700 font-medium">
            Eintrag löschen
          </button>
        </div>
      )}
    </div>
  );
}

// ── Manueller Eintrag ──────────────────────────────────────────────────────

function NeuerEintrag({ mandantId, onSaved }: {
  mandantId: MandantID | null;
  onSaved: () => void;
}): React.JSX.Element {
  const [show, setShow]   = useState(false);
  const [titel, setTitel] = useState('');
  const [inhalt, setInhalt] = useState('');

  if (mandantId === null) return <></>;
  if (!show) {
    return (
      <button onClick={() => { setShow(true); }} className="btn-secondary text-sm">
        + Manueller Eintrag
      </button>
    );
  }
  return (
    <div className="card space-y-3">
      <input type="text" value={titel} onChange={e => { setTitel(e.target.value); }}
        placeholder="Titel" className="input-field" />
      <textarea value={inhalt} onChange={e => { setInhalt(e.target.value); }}
        placeholder="Inhalt / Notiz" rows={4} className="input-field" />
      <div className="flex gap-2">
        <button
          onClick={() => {
            if (!titel.trim()) return;
            void addManuellerEintrag({ mandant_id: mandantId, titel: titel.trim(), inhalt })
              .then(() => { setTitel(''); setInhalt(''); setShow(false); onSaved(); });
          }}
          className="btn-primary text-sm">
          Speichern
        </button>
        <button onClick={() => { setShow(false); }} className="btn-secondary text-sm">Abbrechen</button>
      </div>
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────

export function AkteModul(): React.JSX.Element {
  const mandanten      = useMandantStore(s => s.mandanten);
  const aktiverMandant = useMandantStore(s => s.aktiverMandant);
  const [eintraege, setEintraege] = useState<AkteEintrag[]>([]);
  const [protokolle, setProtokolle] = useState<Protokoll[]>([]);
  const [filterTyp, setFilterTyp] = useState<'ALLE' | AkteEintrag['typ']>('ALLE');
  const [importing, setImporting] = useState(false);
  const [meldung, setMeldung]     = useState<{ text: string; ok: boolean } | null>(null);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const requestFocus = useFocusStore(s => s.request);
  const focusId = useFocusStore(s => (s.module === 'akte' ? s.id : null));
  const clearFocus = useFocusStore(s => s.clear);

  const mandantName = useCallback((id: string): string =>
    mandanten.find(m => m.id === id)?.name ?? id, [mandanten]);

  const refresh = useCallback((): void => {
    void loadAkte(aktiverMandant).then(setEintraege);
    void loadProtokolle(aktiverMandant ?? undefined).then(setProtokolle);
  }, [aktiverMandant]);

  useEffect(() => { refresh(); }, [refresh]);

  // Deep-Link aus der globalen Suche: Eintrag hervorheben + hinscrollen.
  useEffect(() => {
    if (focusId === null) return;
    setFilterTyp('ALLE');
    setHighlightId(focusId);
    clearFocus();
    const t = setTimeout(() => { setHighlightId(null); }, 2500);
    return () => { clearTimeout(t); };
  }, [focusId, clearFocus]);
  useEffect(() => {
    if (highlightId === null) return;
    document.getElementById(`akte-${highlightId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, eintraege]);

  /* Handy-Erfassung (📷-Button) legt Einträge ab → Liste aktualisieren */
  useEffect(() => {
    window.addEventListener('amed:akte-changed', refresh);
    return () => { window.removeEventListener('amed:akte-changed', refresh); };
  }, [refresh]);

  const handleImport = async (): Promise<void> => {
    setImporting(true);
    setMeldung(null);
    const teile: string[] = [];
    let fehler: string[] = [];

    /* Kalender-Notizen */
    try {
      const cfg = await loadSetting<{ calendarId: string } | null>('gcal_config', null);
      const erg: ImportErgebnis = await importBesprechungsnotizen(cfg?.calendarId ?? 'primary');
      teile.push(`Kalender: ${erg.neu} neu (${erg.duplikate} bereits da, ${erg.uebersprungen} ohne Firmen-Treffer)`);
    } catch (e) {
      fehler = [...fehler, `Kalender: ${e instanceof Error ? e.message : String(e)}`];
    }

    /* E-Mails */
    try {
      const erg = await importEmails();
      teile.push(`E-Mail: ${erg.neu} neu (${erg.duplikate} bereits da, ${erg.uebersprungen} ohne Firmen-Treffer)`);
    } catch (e) {
      fehler = [...fehler, `E-Mail: ${e instanceof Error ? e.message : String(e)}`];
    }

    setMeldung({
      text: [...teile, ...fehler].join(' — '),
      ok: fehler.length === 0,
    });
    setImporting(false);
    refresh();
  };

  const handleDelete = (e: AkteEintrag): void => {
    if (!window.confirm(`Eintrag "${e.titel}" aus der Akte löschen?`)) return;
    void deleteAkteEintrag(e.id).then(refresh);
  };

  const handleToggleErledigt = (e: AkteEintrag): void => {
    void toggleAkteErledigt(e.id).then(refresh);
  };

  const requestHandoff = useHandoffStore(s => s.request);
  const handleFormular = (e: AkteEintrag, formularTyp: string): void => {
    requestHandoff('formulare', {
      action: 'formular',
      formularTyp,
      quelle: { datum: e.datum, text: e.inhalt, titel: e.titel },
    });
  };
  const handleBegehung = (e: AkteEintrag): void => {
    requestHandoff('begehung', {
      action: 'begehung',
      quelle: { text: e.inhalt, titel: e.titel },
    });
  };

  const handleBelegeExport = (): void => {
    void belegeAlsCsv(aktiverMandant, false, mandantName).then(csv => {
      const scope = aktiverName !== null ? aktiverName.replace(/\W+/g, '-') : 'alle';
      downloadCsv(csv, `amed-belege-${scope}-${new Date().toISOString().slice(0, 10)}.csv`);
    });
  };

  const belegeVorhanden = eintraege.some(e => e.typ === 'BELEG');

  const sichtbar = filterTyp === 'ALLE'
    ? eintraege
    : eintraege.filter(e => e.typ === filterTyp);

  const aktiverName = aktiverMandant !== null ? mandantName(aktiverMandant) : null;

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-4
                      flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">🗂 Firmen-Akte</h1>
          <p className="text-xs text-gray-400">
            {aktiverName !== null ? `Akte: ${aktiverName}` : 'Alle Mandanten'}
            {' '}(Firma links oben wechseln)
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {(['ALLE', 'BESPRECHUNG', 'EMAIL', 'DOKUMENT', 'BELEG'] as const).map(t => (
            <button key={t} onClick={() => { setFilterTyp(t); }}
              className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors
                ${filterTyp === t
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200'}`}>
              {t === 'ALLE' ? 'Alle' : TYP_META[t].label}
            </button>
          ))}
          {belegeVorhanden && (
            <button onClick={handleBelegeExport}
              className="btn-secondary text-sm" title="Alle Belege als CSV für den Steuerberater">
              🧾 Belege → CSV
            </button>
          )}
          <button onClick={() => { setCaptureOpen(true); }}
            className="btn-secondary text-sm">
            📷 Erfassen
          </button>
          <button onClick={() => { void handleImport(); }} disabled={importing}
            className="btn-primary text-sm disabled:opacity-50">
            {importing ? 'Importiere…' : '⤓ Jetzt importieren'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-3xl mx-auto w-full space-y-3">
        {meldung !== null && (
          <p className={`text-sm rounded-lg p-3 ${meldung.ok
            ? 'bg-green-50 text-green-700 dark:bg-green-950/20 dark:text-green-400'
            : 'bg-amber-50 text-amber-800 dark:bg-amber-950/20 dark:text-amber-400'}`}>
            {meldung.text}
          </p>
        )}

        {protokolle.length > 0 && (
          <div className="card p-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
              📝 Protokolle{aktiverName === null ? ' (alle Firmen)' : ''}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {protokolle.slice(0, 12).map(p => (
                <button key={p.id} onClick={() => { requestFocus('protokolle', p.id); }}
                  className="text-xs px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-950/50 truncate max-w-[16rem]"
                  title={`${p.titel || 'Besprechung'} · ${p.datum}`}>
                  📝 {p.titel || 'Besprechung'} · {p.datum.slice(8, 10)}.{p.datum.slice(5, 7)}.
                </button>
              ))}
            </div>
          </div>
        )}

        <NeuerEintrag mandantId={aktiverMandant} onSaved={refresh} />

        {sichtbar.length === 0 ? (
          <div className="text-center py-12 text-gray-400 space-y-2">
            <p className="text-4xl">🗂</p>
            <p className="text-sm">Noch keine Akten-Einträge.</p>
            <p className="text-xs">
              „Jetzt importieren" holt Besprechungsnotizen aus dem Google-Kalender
              und E-Mails aus Gmail (letzte 30 Tage) und ordnet sie den Firmen zu.
            </p>
          </div>
        ) : (
          sichtbar.map(e => (
            <div key={e.id} id={`akte-${e.id}`}
              className={`rounded-xl transition-shadow ${highlightId === e.id ? 'ring-2 ring-blue-400' : ''}`}>
              <EintragKarte eintrag={e}
                mandantName={mandantName(e.mandant_id)} onDelete={handleDelete}
                onToggleErledigt={handleToggleErledigt} onFormular={handleFormular}
                onBegehung={handleBegehung} />
            </div>
          ))
        )}
      </div>

      {captureOpen && (
        <CaptureModal onClose={() => { setCaptureOpen(false); }} onSaved={refresh} />
      )}
    </div>
  );
}
